/**
 * Subdomain utilities for client-level subdomain routing.
 *
 * Production: {client}.traineryhcm.com
 * Local dev:  {client}.localhost:3004
 *
 * BASE_DOMAIN is 'localhost' in dev, 'traineryhcm.com' in production.
 */

const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'traineryhcm.com';
const SUBDOMAIN_ROUTING_ENABLED = import.meta.env.VITE_ENABLE_SUBDOMAIN_ROUTING === 'true';

/**
 * Check if we're running on localhost (dev mode).
 */
function isLocalhost(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');
}

/**
 * Get the base/root domain for cookie sharing.
 * Returns '.traineryhcm.com' in production, '.localhost' in dev (for cross-subdomain cookies).
 */
export function getBaseDomain(): string {
  if (!SUBDOMAIN_ROUTING_ENABLED) return '';
  if (isLocalhost()) {
    return '.localhost';
  }
  const hostname = window.location.hostname;
  if (hostname.endsWith(BASE_DOMAIN)) {
    return `.${BASE_DOMAIN}`;
  }
  return '';
}

/**
 * Extract the subdomain from the current URL.
 *
 * Production: 'acme.traineryhcm.com' -> 'acme'
 * Local dev:  'acme.localhost' -> 'acme'
 *
 * Returns null if on the root domain (no subdomain).
 */
export function getCurrentSubdomain(): string | null {
  if (!SUBDOMAIN_ROUTING_ENABLED) return null;
  const hostname = window.location.hostname;

  // Local dev: extract from {sub}.localhost
  if (hostname.endsWith('.localhost')) {
    const sub = hostname.slice(0, -'.localhost'.length);
    if (!sub || sub === 'www' || sub === 'apps') return null;
    return sub;
  }

  // Bare localhost or 127.0.0.1 = no subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Production: extract from {sub}.traineryhcm.com
  if (!hostname.endsWith(BASE_DOMAIN)) {
    return null;
  }
  const sub = hostname.slice(0, -(BASE_DOMAIN.length + 1)); // remove '.traineryhcm.com'
  if (!sub || sub === 'www' || sub === 'apps') {
    return null;
  }
  return sub;
}

/**
 * Check if we're on the main/root domain (not a client subdomain).
 */
export function isMainDomain(): boolean {
  if (!SUBDOMAIN_ROUTING_ENABLED) return true;
  return getCurrentSubdomain() === null;
}

/**
 * Build a URL for a specific client subdomain.
 * e.g., buildSubdomainUrl('acme', '/home') -> 'https://acme.traineryhcm.com/home'
 * Local: buildSubdomainUrl('acme', '/home') -> 'http://acme.localhost:3004/home'
 */
export function buildSubdomainUrl(clientDomain: string, path: string = '/'): string {
  if (!SUBDOMAIN_ROUTING_ENABLED) return path;
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';

  if (isLocalhost()) {
    return `${protocol}//${clientDomain}.localhost${port}${path}`;
  }

  return `${protocol}//${clientDomain}.${BASE_DOMAIN}${port}${path}`;
}

/**
 * Build the main domain login URL.
 * Production: apps.traineryhcm.com/login
 * Local: localhost:3004/login
 */
export function getMainDomainLoginUrl(params?: string): string {
  if (!SUBDOMAIN_ROUTING_ENABLED) return `/login${params ? `?${params}` : ''}`;
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';

  if (isLocalhost()) {
    return `${protocol}//localhost${port}/login${params ? `?${params}` : ''}`;
  }

  return `${protocol}//apps.${BASE_DOMAIN}${port}/login${params ? `?${params}` : ''}`;
}

/**
 * Get the appropriate login URL based on current location.
 * - If on a client subdomain (e.g., acme.traineryhcm.com), stay there.
 * - If on main domain, go to main domain login.
 * This keeps users on their tenant's login page after logout.
 */
export function getLoginUrl(params?: string): string {
  if (!SUBDOMAIN_ROUTING_ENABLED) return `/login${params ? `?${params}` : ''}`;
  if (getCurrentSubdomain()) {
    return `/login${params ? `?${params}` : ''}`;
  }
  return getMainDomainLoginUrl(params);
}

/**
 * Check if a given account domain matches the current subdomain.
 * Returns true if on main domain (no validation needed) or if domains match.
 */
export function doesAccountMatchSubdomain(accountDomain: string | undefined): boolean {
  if (!SUBDOMAIN_ROUTING_ENABLED) return true;
  const currentSubdomain = getCurrentSubdomain();
  if (!currentSubdomain) return true; // On main domain, no check needed
  if (!accountDomain) return !currentSubdomain;
  return currentSubdomain === accountDomain;
}
